import { useState, useEffect, useCallback, useRef } from 'react';
import { isDesktop } from '../utils/desktop';
import { verificarAtualizacao, aplicarAtualizacao } from '../utils/updater';

// Espera antes da checagem no boot: deixa a janela pintar e o login resolver
// primeiro. Checar junto com o carregamento inicial competiria por rede com as
// chamadas de autenticação, que são o que o usuário está esperando.
const ATRASO_BOOT_MS = 4000;

// Recheca de tempos em tempos: as lojas deixam o app aberto o dia inteiro, então
// depender só do boot faria uma correção publicada de manhã só chegar no dia seguinte.
const INTERVALO_MS = 30 * 60 * 1000;

/**
 * Verificação de atualização da versão desktop.
 *
 * No navegador é inerte: `estado` fica 'indisponivel' e nada é agendado.
 *
 * Deliberadamente NÃO aplica sozinho. Atualizar reinicia o processo, e fazer isso
 * no meio de uma transferência em digitação perderia o trabalho do usuário. Quem
 * decide a hora é ele.
 */
export const useAtualizacao = () => {
  const [info, setInfo] = useState({ estado: 'indisponivel' });
  const [aplicando, setAplicando] = useState(false);
  const [erro, setErro] = useState('');
  const dispensadoRef = useRef(false);

  const checar = useCallback(async () => {
    if (!isDesktop()) return { estado: 'indisponivel' };
    const r = await verificarAtualizacao();   // nunca lança
    if (!dispensadoRef.current) setInfo(r);
    return r;
  }, []);

  useEffect(() => {
    if (!isDesktop()) return undefined;
    const inicial = setTimeout(checar, ATRASO_BOOT_MS);
    const periodico = setInterval(checar, INTERVALO_MS);
    return () => { clearTimeout(inicial); clearInterval(periodico); };
  }, [checar]);

  const aplicar = useCallback(async () => {
    setAplicando(true);
    setErro('');
    try {
      await aplicarAtualizacao();  // reinicia o processo; nada roda depois
    } catch (e) {
      // Falha comum: pasta sem permissão de escrita (instalado em Program Files),
      // caso em que o resources.neu não pode ser sobrescrito.
      setErro(e?.message || 'Não foi possível aplicar a atualização.');
      setAplicando(false);
    }
  }, []);

  const dispensar = useCallback(() => {
    dispensadoRef.current = true;
    setInfo({ estado: 'atualizado' });
  }, []);

  return {
    ...info,
    aplicando,
    erro,
    checar,
    aplicar,
    dispensar,
    temNovidade: info.estado === 'disponivel' || info.estado === 'exige-reinstalacao',
  };
};
